import { createFeatureSelector, createSelector } from '@ngrx/store';

import {
  extractCodeFromCodeableConcept,
  selectCarePlanById,
  selectQnrResponses,
  selectQuestionnaires,
  splitRelativeReference,
} from '@hestia/ngx-fhir';

import { qnrInfo } from '../../../assets/i18n';
import {
  IDawbaCarePlan,
  IDawbaCarePlanActivity,
  PSDQ_QNR_ID,
  SSDQ_QNR_ID,
  TSDQ_QNR_ID,
  SINTRO_QNR_ID,
  PINTRO_QNR_ID,
  TINTRO_QNR_ID,
  SFINISH_QNR_ID,
  PFINISH_QNR_ID,
  TFINISH_QNR_ID
} from '../../models/models';
import * as fromDawbaSpecific from './dawba-specific.reducer';

export const selectDawbaSpecificState =
  createFeatureSelector<fromDawbaSpecific.IDawbaSpecificState>(
    fromDawbaSpecific.dawbaSpecificFeatureKey
  );

export const selectDawbaCarePlan = (id: string) =>
  createSelector(
    selectQuestionnaires,
    selectQnrResponses,
    selectCarePlanById(id),
    (questionnaires, qnrResponses, carePlan) => {
      if (!carePlan) {
        return null;
      }
      const activities: IDawbaCarePlanActivity[] = carePlan.activity.map(
        (rawActivity) => {
          const questionnaireCanonical =
            rawActivity.detail.instantiatesCanonical[0] ?? null;
          const split = questionnaireCanonical.split('/');
          const qnrCanonicalId = split[split.length - 1]; // TODO: Undo this implementation
          const qnrResponseRefererence: fhir4.Reference =
            rawActivity.outcomeReference?.[0] ?? null;
          const qnrResponseId: string =
            qnrResponseRefererence?.reference?.split('/')?.[1] ?? null;
          const questionnaire = questionnaires.find(
            (elem) => elem.id === qnrCanonicalId
          );
          const questionnaireResponse =
            qnrResponses.find((elem) => elem.id === qnrResponseId) ?? null;
          const code = extractCodeFromCodeableConcept(rawActivity.detail.code);
          const activity: IDawbaCarePlanActivity = {
            carePlanId: carePlan.id,
            fhirElem: rawActivity,
            qnrCanonical: questionnaireCanonical,
            qnrResource: questionnaire,
            qnrResponseResource: questionnaireResponse,
            code,
            status: rawActivity.detail.status,
            i18nTitle: qnrInfo[questionnaire?.id]?.title ?? questionnaire.title,
            i18nInfo: qnrInfo[questionnaire?.id]?.info ?? questionnaire.title,
            extension:rawActivity.extension
          };
          return activity;
        }
      );
      const activitiesByCode = new Map();
      activities.forEach((activity) =>
        activitiesByCode.set(activity.code, activity)
      );
      const dashboardActivities = activities.filter(
        (x) => ![/*SSDQ_QNR_ID, PSDQ_QNR_ID, TSDQ_QNR_ID*/].includes(x.code)//Ricardo commented- so SDQ is included in the menu
      ); // The QNR ID is also used as the activity code
      const subjectDetails = splitRelativeReference(carePlan.subject.reference);
      const dawbaCarePlan: IDawbaCarePlan = {
        subjectType: subjectDetails.resourceType,
        subjectId: subjectDetails.resourceId,
        carePlanResource: carePlan,
        allActivities: activities,
        totalActivityCount: activities.length,
        completedActivityCount: activities.filter(
          (elem) => elem.status === 'completed'
        ).length,
        inProgressActivityCount: activities.filter(
          (elem) => elem.status === 'in-progress'
        ).length,
        finishActivity:
          activitiesByCode.get(SFINISH_QNR_ID) ??
          activitiesByCode.get(PFINISH_QNR_ID) ??
          activitiesByCode.get(TFINISH_QNR_ID) ??
          null,
        introActivity:
          activitiesByCode.get(SINTRO_QNR_ID) ??
          activitiesByCode.get(PINTRO_QNR_ID) ??
          activitiesByCode.get(TINTRO_QNR_ID) ??
          null,
        sdqActivity:
          activitiesByCode.get(SSDQ_QNR_ID) ??
          activitiesByCode.get(PSDQ_QNR_ID) ??
          activitiesByCode.get(TSDQ_QNR_ID) ??
          null,
        activitiesByCode: activitiesByCode,
        dashboardActivities,
        dashboardActivityCount: dashboardActivities.length,
        dashboardCompletedActivityCount: dashboardActivities.filter(
          (elem) => elem.status === 'completed'
        ).length,
        dashboardInProgressActivityCount: dashboardActivities.filter(
          (elem) => elem.status === 'in-progress'
        ).length,
      };
      return dawbaCarePlan;
    }
  );

export const selectSsdqCarePlanActivity = (id: string) =>
  createSelector(selectDawbaCarePlan(id), (carePlan) => {
    if (!carePlan) {
      return null;
    }
    return carePlan.sdqActivity ?? null;
  });

export const selectIsCarePlanLoaded = createSelector(
  selectDawbaSpecificState,
  (state) => state.carePlanLoaded
);
